import { IWebDevice } from '../../interfaces/auth.interface';

export type LoginView = 'contact' | 'otp' | 'password' | 'forgotPassword' | 'recovery' | 'trusted' | 'restore';
export type RecoveryStepKey = 'password' | 'email' | 'phone';
export type TrustedStage = 'ask' | 'selectDevice' | 'code';
export type AppRedirectResult = 'redirected' | 'unauthorized' | 'failed';
export type ContactKind = 'phone' | 'email';

export interface IRecoveryStepDef {
  index: number;
  key: RecoveryStepKey;
  label: string;
}

export interface IState {
  view: LoginView;
  otp: string;
  otpError: string;
  otpSentTo: string;
  countdown: number;
  contactError: string;
  passwordError: string;
}

export const initialState: IState = {
  view: 'contact',
  otp: '',
  otpError: '',
  otpSentTo: '',
  countdown: 60,
  contactError: '',
  passwordError: '',
};

export interface IRecoveryState {
  step: number;
  done: { password: boolean; email: boolean; phone: boolean };
  otpStage: boolean;
  otpSentTo: string;
  error: string;
}

export const initialRecoveryState: IRecoveryState = {
  step: 1,
  done: { password: false, email: false, phone: false },
  otpStage: false,
  otpSentTo: '',
  error: '',
};

export interface ITrustedState {
  stage: TrustedStage;
  primaryDeviceId: string;
  primaryDeviceName: string;
  primaryDeviceLocation: string;
  limitReached: boolean;
  webDevices: IWebDevice[];
  selectedRemoveDeviceId: string;
  code: string;
  error: string;
  loadingAction: 'trust' | 'untrusted' | 'verify' | null;
}

export const initialTrustedState: ITrustedState = {
  stage: 'ask',
  primaryDeviceId: '',
  primaryDeviceName: '',
  primaryDeviceLocation: '',
  limitReached: false,
  webDevices: [],
  selectedRemoveDeviceId: '',
  code: '',
  error: '',
  loadingAction: null,
};

export interface IRestoreState {
  deviceId: string;
  name: string;
  familyCode: string;
  challenge: string;
  error: string;
  loadingAction: 'restore' | 'revoke' | null;
}

export const initialRestoreState: IRestoreState = {
  deviceId: '',
  name: '',
  familyCode: '',
  challenge: '',
  error: '',
  loadingAction: null,
};

export interface IContactItem {
  kind: ContactKind;
  value: string;
  href: string;
}

export interface IContactGroup {
  label: string;
  items: IContactItem[];
}

export interface ICountryConfig {
  id: string;
  title: string;
  contactType: ContactKind;
  contactGroups: IContactGroup[];
}

export const PHONE_LENGTH = 10;
export const PHONE_PATTERN = /^[0-9]{10}$/;
export const RECOVERY_PHONE_PATTERN = /^[0-9]{6,15}$/;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const COUNTRIES: ICountryConfig[] = [
  {
    id: 'INDIA',
    title: 'India',
    contactType: 'phone',
    contactGroups: [
      {
        label: 'For oblation',
        items: [
          { kind: 'phone', value: '+919153963392', href: 'tel:+919153963392' },
          { kind: 'email', value: 'oblation@satsang.org.in', href: 'mailto:oblation@satsang.org.in' },
        ],
      },
      {
        label: 'For accommodation',
        items: [
          { kind: 'phone', value: '+919153963396', href: 'tel:+919153963396' },
          {
            kind: 'email',
            value: 'support@acco.satsangphilanthropy.com',
            href: 'mailto:support@acco.satsangphilanthropy.com',
          },
        ],
      },
      {
        label: 'Anything else',
        items: [{ kind: 'phone', value: '18003450122', href: 'tel:18003450122' }],
      },
    ],
  },
  {
    id: 'NEPAL',
    title: 'Nepal',
    contactType: 'phone',
    contactGroups: [
      {
        label: 'Contact us',
        items: [
          { kind: 'phone', value: '+977-9768615361', href: 'tel:+9779768615361' },
          {
            kind: 'email',
            value: 'satsangnepalphilanthropy@gmail.com',
            href: 'mailto:satsangnepalphilanthropy@gmail.com',
          },
        ],
      },
    ],
  },
  {
    id: 'USA',
    title: 'USA',
    contactType: 'phone',
    contactGroups: [
      {
        label: 'Contact Us',
        items: [
          { kind: 'phone', value: '+1 317-480-3184', href: 'tel:+13174803184' },
          {
            kind: 'email',
            value: 'TechSupport@SatsangAmerica.org',
            href: 'mailto:TechSupport@SatsangAmerica.org',
          },
        ],
      },
    ],
  },
];

export const DEFAULT_COUNTRY_ID = 'INDIA';

export function findCountry(id: string | null | undefined): ICountryConfig | null {
  if (!id) {
    return null;
  }
  return COUNTRIES.find(country => country.id === id) ?? null;
}
